import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookMarked, FileText, Film, Image as ImageIcon, X } from "lucide-react";

const iconFor = (t = "") => (t.startsWith("video/") ? Film : t.startsWith("image/") ? ImageIcon : FileText);

export const FileLibraryPicker = ({ selectedId, onPick, disabled }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: files = [] } = useQuery({ queryKey: ["file-library"], queryFn: async () => (await api.get("/shared-files/library")).data });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/shared-files/library/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["file-library"] }); toast.success("Removed from saved files"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove"),
  });

  return (
    <div data-testid="file-library">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        <BookMarked className="h-3.5 w-3.5" /> Saved files
      </div>
      {files.length === 0 ? (
        <p data-testid="file-library-empty" className="text-xs text-slate-400">No saved files yet. Attach a file below and tick "Save to saved files" to reuse it.</p>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
          {files.map((f) => {
            const Icon = iconFor(f.content_type);
            const active = selectedId === f.id;
            const canRemove = user?.role === "admin" || [f.uploaded_by, f.added_by].includes(user?.email);
            return (
              <span key={f.id} className={`inline-flex items-center rounded-full border text-xs transition-colors ${active ? "bg-[#0B1F44] border-[#0B1F44] text-white" : "border-slate-200 text-slate-700 hover:border-[#0B1F44]"}`}>
                <button data-testid={`file-library-item-${f.id}`} disabled={disabled} onClick={() => onPick(active ? null : f)}
                  title={f.filename} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1">
                  <Icon className="h-3.5 w-3.5" />{f.name}
                </button>
                {canRemove && (
                  <button data-testid={`file-library-remove-${f.id}`} disabled={disabled || remove.isPending}
                    onClick={() => { if (window.confirm(`Remove "${f.name}" from saved files? Links already sent keep working.`)) remove.mutate(f.id); }}
                    aria-label={`Remove ${f.name}`}
                    className={`p-1.5 pr-2 ${active ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-red-600"}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
